import { X, Edit2 } from 'lucide-react';
import { Button } from '@predoc/cow';
import { ProviderFacilityFormData } from '../suggested-providers-facility/validation';
import { ProviderFacilityModal } from '../suggested-providers-facility/ProviderFacilityModal';
import { isValidUsState } from '@/feature/patients/utils/us-state';

interface ProviderFacilityListProps {
  providers: ProviderFacilityFormData[];
  onEdit: (index: number, updatedProvider: ProviderFacilityFormData) => void;
  onRemove: (index: number) => void;
}

export const ProviderFacilityList = ({
  providers,
  onEdit,
  onRemove,
}: ProviderFacilityListProps) => {
  const getRecordTypeLabel = (recordsRequested: string) => {
    switch (recordsRequested) {
      case 'complete':
        return 'Complete Record Retrieval';
      case 'specific':
        return 'Specific Medical Records';
      default:
        return 'Complete Record Retrieval';
    }
  };

  if (providers.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {providers.map((provider: ProviderFacilityFormData, index: number) => (
        <div
          key={index}
          className="flex items-center justify-between rounded-lg border bg-gray-50 px-4 py-3"
        >
          <div>
            <span className="text-sm font-medium text-gray-900">
              {provider.providerName || 'N/A'} -{' '}
              {provider.facilityName || 'N/A'}(
              {getRecordTypeLabel(provider.recordsRequested)})
            </span>
            {provider.state && !isValidUsState(provider.state) && (
              <p className="text-destructive mt-1 text-sm">
                State/Province &quot;{provider.state}&quot; is not supported.
                Edit this provider and enter a valid US code or clear the field.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ProviderFacilityModal
              title="Edit Provider/Facility"
              submitButtonText="Save Changes"
              clearButtonText="Clear Form"
              showAddAnotherButton={false}
              initialValues={provider}
              onSubmit={(updatedProvider) => onEdit(index, updatedProvider)}
            >
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground size-8 p-0"
                aria-label="Edit provider"
              >
                <Edit2 size={16} />
              </Button>
            </ProviderFacilityModal>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemove(index)}
              className="h-8 w-8 p-0 text-gray-500"
              aria-label="Remove provider"
            >
              <X size={16} />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
